import React, { useCallback } from 'react';
import { LayoutAddComponentListItem } from '@website-editor/features/custom-page/components/LeftPanelLayouts/LayoutAddComponentListItem';
import { useCreateNewJsonComponent } from '../hooks/useJsonBlobStorageUtils';
import { AdminSidebarComponentProps } from '../JsonComponent.types';
import { SimpleGrabHandle } from '@website-editor/components/SimpleGrabHandle';
import { FaqConfig } from './Faq.config';
import { NotificationBubble } from '@withjoy/joykit/icons';
import { TextV2 } from '@withjoy/joykit';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { JsonDataBlobComponentFragment, JsonDataContainerFragment } from '@graphql/generated';
import { useTranslation } from '@shared/core';
import { FaqData } from './Faq.types';
import { useGoToEditorView } from '../hooks/useJsonComponentNavigation';
import { useWebsiteEditorCustomBlocksConfig } from '../hooks/useWebsiteEditorCustomBlocksConfig';

export const FaqGrabber = (props: AdminSidebarComponentProps) => {
  const { tq } = useTranslation('faqCustomComponent');
  const faqData = props.componentData.containerContent.content as FaqData;
  const itemCount = faqData?.items?.length || 0;

  const customBlocksConfig = useWebsiteEditorCustomBlocksConfig(tq);
  const faqConfigs = customBlocksConfig?.faq;

  return (
    <SimpleGrabHandle
      title={faqConfigs?.componentTitle || tq`FAQs`}
      subTitle={itemCount === 1 ? tq`${itemCount} item` : tq`${itemCount} items`}
      icon={<NotificationBubble size={32} />}
      {...props}
    ></SimpleGrabHandle>
  );
};

export const FaqAdd = (props: AdminSidebarComponentProps) => {
  const { tq } = useTranslation('faqCustomComponent');
  const { createNewJsonComponent } = useCreateNewJsonComponent(props.eventHandle);
  const { setRefetchQueries } = useCustomComponentStateContext<JsonDataContainerFragment>();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { goToEditorView } = useGoToEditorView();

  const customBlocksConfig = useWebsiteEditorCustomBlocksConfig(tq);
  const faqConfigs = customBlocksConfig?.faq;

  const handleClick = useCallback(async () => {
    const initialData: FaqData = {
      items: [],
    };

    const result = await createNewJsonComponent({
      schemaURL: FaqConfig.schemaUrl,
      type: FaqConfig.componentType,
      initialData,
      containerId: props.containerId,
      sortOrder: props.componentData?.sortOrder,
    });
    if (result?.componentData && result.containerData?.data) {
      setRefetchQueries(['PageContainerBySlug']);
      updateWebsitePreview();
      // Construct full fragment combining component and container data
      const fullComponentData: JsonDataBlobComponentFragment = {
        ...result.componentData,
        __typename: 'JsonDataBlobComponent',
        containerContent: result.containerData.data,
      };
      goToEditorView(fullComponentData);
    }
  }, [createNewJsonComponent, goToEditorView, props.componentData?.sortOrder, props.containerId, setRefetchQueries, updateWebsitePreview]);

  return (
    <LayoutAddComponentListItem onClick={handleClick}>
      <NotificationBubble size={48} />
      <TextV2
        typographyVariant="hed2"
        textAlign="left"
      >
        {faqConfigs?.componentTitle || tq`FAQs`}
      </TextV2>
    </LayoutAddComponentListItem>
  );
};
